/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { CheckCircle2, FileCode2, ListFilter, ListTree, SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';

/* Import configuration */
import { HAS_NEUTRAUBLING_SENSOR_DATA } from '@/config/csvSensors';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import {
    DeviceDefinitionTextGenerator,
    type DeviceDefinitionAvailability,
    type DeviceDefinitionCodeType,
    type DeviceDefinitionSupplyType,
    type DeviceDefinitionSupplyTypeMode,
    type DeviceDefinitionSupplyTypeOption,
} from '@/lib/sensors/demo/deviceDefinitionTextGenerator';
import type { SensorDeviceCustomer } from '@/lib/sensors/demo/demoDeviceSensors';

interface DeviceDefinitionsTabProps {
    theme: 'light' | 'dark';
}

const SYSTEMS_CONFIG_TO_DEVICE_DEFINITIONS_CODE = `foreach (SYSTEMS as $system) {
    $sensors = $system['sensors'];

    $devices[] = Building::create(
        urn: $system['urn'],
        description: $system['description'],
        uiName: 'Building',
        initialTime: $initialTime,
        address: $system['address'],
        grid_feed: $sensors['grid_feed'][0] ?? null,
        grid_supply: $sensors['grid_supply'][0] ?? null,
        pv: $sensors['pv'][0] ?? null,
        heat: $sensors['heat'][0] ?? null,
        gas: $sensors['gas'][0] ?? null,
        water: $sensors['water'][0] ?? null,
    );
}`;

function DeviceDefinitionsTab({ theme }: DeviceDefinitionsTabProps) {
    const { t } = useI18n();
    const [selectedCustomer, setSelectedCustomer] = useState<SensorDeviceCustomer>(
        HAS_NEUTRAUBLING_SENSOR_DATA ? 'neutraubling' : 'muenster',
    );
    const [selectedSupplyTypeMode, setSelectedSupplyTypeMode] = useState<DeviceDefinitionSupplyTypeMode>('all');
    const [selectedSupplyTypes, setSelectedSupplyTypes] = useState<DeviceDefinitionSupplyType[]>([]);
    const [selectedAvailability, setSelectedAvailability] = useState<DeviceDefinitionAvailability>('enabled');
    const [selectedCodeType, setSelectedCodeType] = useState<DeviceDefinitionCodeType>('direct');
    const supplyTypeFilterOptions = ([
        { label: t('Electricity'), value: 'BL' },
        { label: t('Gas'), value: 'GAS' },
        { label: t('Grid Feed'), value: 'GRID_FEED' },
        { label: t('Heat'), value: 'HE' },
        { label: t('Photovoltaics'), value: 'PV' },
        { label: t('Water'), value: 'WATER' },
    ] satisfies DeviceDefinitionSupplyTypeOption[]).sort((left, right) => left.label.localeCompare(right.label));
    const virtualDeviceOutput = useMemo(() => {
        return new DeviceDefinitionTextGenerator({
            availability: selectedAvailability,
            codeType: selectedCodeType,
            customer: selectedCustomer,
            selectedSupplyTypes,
            supplyTypeMode: selectedSupplyTypeMode,
            supplyTypeOptions: supplyTypeFilterOptions,
        }).generate();
    }, [selectedAvailability, selectedCodeType, selectedCustomer, selectedSupplyTypeMode, selectedSupplyTypes, supplyTypeFilterOptions]);

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Device Definitions')}</h2>
                <p>{t('Select a customer and group filter to generate virtual device definitions.')}</p>
            </section>

            <section className="dashboard-panel flex flex-col items-stretch justify-start gap-4 p-5">
                <div className={`grid w-full grid-cols-1 gap-y-4 text-left md:divide-x md:divide-base-content ${
                    selectedSupplyTypeMode === 'all' ? 'md:grid-cols-4' : 'md:grid-cols-3'
                }`}>
                    <div className="flex min-h-12 items-center gap-3 md:pr-5">
                        <label className="text-sm font-semibold" htmlFor="device-definitions-customer-select">
                            {t('Customer')}
                        </label>
                        <select
                            id="device-definitions-customer-select"
                            className="select select-bordered w-auto"
                            value={selectedCustomer}
                            onChange={(event) => {
                                setSelectedCustomer(event.target.value as SensorDeviceCustomer);
                            }}
                        >
                            {HAS_NEUTRAUBLING_SENSOR_DATA ? (
                                <option value="neutraubling">{t('Neutraubling')}</option>
                            ) : null}
                            <option value="muenster">{t('Münster')}</option>
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 md:px-5">
                        <span className="text-sm font-semibold">{t('Supply type')}</span>
                        <label className="toggle border border-base-300 bg-base-200 text-base-content">
                            <input
                                type="checkbox"
                                checked={selectedSupplyTypeMode === 'individual'}
                                onChange={(event) => {
                                    setSelectedSupplyTypeMode(event.target.checked ? 'individual' : 'all');
                                }}
                                aria-label={t('Supply type')}
                            />
                            <ListTree className="h-4 w-4" aria-hidden="true" />
                            <ListFilter className="h-4 w-4" aria-hidden="true" />
                        </label>
                        <span className="w-24 text-sm">{selectedSupplyTypeMode === 'all' ? t('All') : t('Individual')}</span>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 md:px-5">
                        <span className="text-sm font-semibold">{t('Status')}</span>
                        <label className="toggle border border-base-300 bg-base-200 text-base-content">
                            <input
                                type="checkbox"
                                checked={selectedAvailability === 'all'}
                                onChange={(event) => {
                                    setSelectedAvailability(event.target.checked ? 'all' : 'enabled');
                                }}
                                aria-label={t('Status')}
                            />
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                        </label>
                        <span className="w-24 text-sm">{selectedAvailability === 'enabled' ? t('Enabled') : t('All')}</span>
                    </div>

                    {selectedSupplyTypeMode === 'all' ? (
                        <div className="flex min-h-12 items-center gap-3 md:pl-5">
                            <span className="text-sm font-semibold">{t('Code type')}</span>
                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                <input
                                    type="checkbox"
                                    checked={selectedCodeType === 'config'}
                                    onChange={(event) => {
                                        setSelectedCodeType(event.target.checked ? 'config' : 'direct');
                                    }}
                                    aria-label={t('Code type')}
                                />
                                <FileCode2 className="h-4 w-4" aria-hidden="true" />
                                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                            </label>
                            <span className="w-24 text-sm">{selectedCodeType === 'direct' ? t('Direct') : t('Config')}</span>
                        </div>
                    ) : null}
                </div>
            </section>

            {selectedSupplyTypeMode === 'individual' ? (
                <section className="dashboard-panel flex items-stretch justify-start p-5">
                    <div className="flex w-full items-center gap-3 text-left">
                        <label className="text-sm font-semibold" htmlFor="device-definitions-supply-types-select">
                            {t('Supply types')}:
                        </label>
                        <select
                            multiple
                            id="device-definitions-supply-types-select"
                            className="select select-bordered scrollbar-hidden h-12 w-auto"
                            value={selectedSupplyTypes}
                            onChange={(event) => {
                                setSelectedSupplyTypes(Array.from(
                                    event.currentTarget.selectedOptions,
                                    (option) => option.value as DeviceDefinitionSupplyType,
                                ));
                            }}
                        >
                            {supplyTypeFilterOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </section>
            ) : null}

            <section className="dashboard-panel flex flex-col gap-4 p-5">
                <div className="text-sm font-semibold">
                    {t('Configuration for')}{' '}
                    <code className="font-mono">
                        {selectedSupplyTypeMode === 'all'
                            ? selectedCodeType === 'config'
                                ? 'Systems.php'
                                : 'DeviceDefinition.php'
                            : 'UrnReplacementMap.php'}
                    </code>
                </div>
                <CodeBlock
                    className="min-h-[40rem]"
                    code={virtualDeviceOutput}
                    language="php"
                    theme={theme}
                    wrapperClassName="w-full"
                />
            </section>

            {selectedSupplyTypeMode === 'all' && selectedCodeType === 'config' ? (
                <section className="dashboard-panel flex flex-col gap-4 p-5">
                    <div className="text-sm font-semibold">
                        {t('Configuration for')} <code className="font-mono">DeviceDefinition.php</code>
                    </div>
                    <CodeBlock
                        code={SYSTEMS_CONFIG_TO_DEVICE_DEFINITIONS_CODE}
                        language="php"
                        theme={theme}
                        wrapperClassName="w-full"
                    />
                </section>
            ) : null}
        </div>
    );
}

export default DeviceDefinitionsTab;
